import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bell, CalendarDays, ChevronDown, Flag, Star, X } from "lucide-react";
import { PRIORITY_LABEL, type Destination, type Priority, type TaskStatus } from "@desk/contracts";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Kbd } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PriorityIcon } from "@/components/icons";
import { useHierarchy } from "@/data/hooks";
import { useTaskActions } from "@/data/actions";
import { describeError } from "@/data/api";
import { formatDay, formatInstant } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { getStored, setStored } from "@/lib/storage";
import { DEFAULTS, PREF, type CaptureDefault } from "@/lib/prefs";
import { openTask, ui, useUi } from "@/features/ui-state";
import { DestIcon, DestinationPicker, destLabel, normalizeDest, savedDest, type Dest } from "@/features/destination/DestinationPicker";
import { PriorityRadioItems } from "@/features/task/TaskMenu";
import { ReminderPicker } from "@/features/task/ReminderPicker";

export const chipClass =
  "inline-flex h-7 max-w-[220px] items-center gap-1.5 rounded-md border border-border bg-card px-2 text-[13px] text-foreground/85 transition-colors hover:bg-hover data-[empty=true]:border-dashed data-[empty=true]:text-muted-foreground [&_svg]:size-3.5 [&_svg]:shrink-0";

interface Draft {
  clientId: string;
  title: string;
  notes: string;
  dest: Dest;
  priority: Priority;
  when: string | null;
  deadline: string | null;
  reminderAt: string | null;
  status?: TaskStatus;
}

/**
 * Settings → New tasks go to, used when quick add opens outside a container. Archived or deleted
 * destinations fall back to Inbox (savedDest).
 */
export function captureFallback(): Destination | undefined {
  const pref = getStored<CaptureDefault>(PREF.captureDefault, DEFAULTS.captureDefault);
  if (pref.kind === "pinned") return pref.dest;
  if (pref.kind === "last") return getStored<Destination | undefined>(PREF.captureLast, undefined);
  return undefined;
}

export function QuickAdd() {
  const { open, preset } = useUi((s) => s.quickAdd);
  const current = useUi((s) => s.currentDestination);
  const h = useHierarchy();
  const actions = useTaskActions();
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New draft each time the dialog opens; keep text if it was closed by an error path.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setDraft((prev) => {
      if (prev && prev.title.trim()) return prev; // preserve unsaved text
      return {
        clientId: crypto.randomUUID(),
        title: "",
        notes: "",
        // A preset without a destination (e.g. only a status) keeps the screen's destination;
        // outside a container, the Settings default applies.
        dest:
          preset && (preset.areaId !== undefined || preset.spaceId !== undefined || preset.projectId !== undefined)
            ? normalizeDest(preset, h)
            : current
              ? normalizeDest(current, h)
              : savedDest(captureFallback(), h),
        priority: "none",
        when: preset?.planToday ? h.today : null,
        deadline: null,
        reminderAt: null,
        status: preset?.status,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => ui.set({ quickAdd: { open: false } });
  const update = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  const submit = async () => {
    if (!draft || submitting) return;
    const title = draft.title.trim();
    if (!title) {
      titleRef.current?.focus();
      return;
    }
    setSubmitting(true);
    setError(null);
    const d = draft;
    const result = await actions.create({
      clientId: d.clientId,
      title,
      description: d.notes.trim() || undefined,
      areaId: d.dest.areaId,
      spaceId: d.dest.spaceId,
      projectId: d.dest.projectId,
      priority: d.priority,
      deadline: d.deadline,
      reminderAt: d.reminderAt,
      planDate: d.when ?? undefined,
      status: d.status,
    });
    setSubmitting(false);
    if (result.status === "failed") {
      setError(describeError(result.error));
      return; // keep dialog + text
    }
    setDraft(null);
    close();
    setStored(PREF.captureLast, d.dest);
    const created = result.status === "synced" ? result.task : undefined;
    const where = destLabel(d.dest, h);
    if (result.status === "queued") {
      toast("Saved on this device", { description: "It will sync when Desk is reachable." });
    } else if (d.priority === "urgent" && !d.deadline && !d.when && created) {
      toast(`Urgent task added to ${where}`, {
        description: created.readableId,
        duration: 8000,
        action: { label: "Add to Today", onClick: () => void actions.planAdd([created.id]) },
      });
    } else if (created) {
      toast(`Added ${created.readableId} to ${where}`, {
        action: { label: "Open", onClick: () => openTask(created.id) },
      });
    }
  };

  if (!draft) return null;
  const empty = (v: unknown) => (v ? "false" : "true");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent
        position="top"
        hideClose
        className="max-w-[580px] p-0"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          titleRef.current?.focus();
        }}
      >
        <DialogTitle className="sr-only">New task</DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="px-5 pb-2 pt-4">
            <textarea
              ref={titleRef}
              aria-label="Task title"
              placeholder="New task"
              rows={1}
              value={draft.title}
              maxLength={500}
              onChange={(e) => update({ title: e.target.value.replace(/\n/g, " ") })}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  void submit();
                }
              }}
              className="field-sizing-content block max-h-40 w-full resize-none bg-transparent font-sans text-[18px] font-medium leading-snug text-foreground outline-none placeholder:font-normal placeholder:text-subtle"
            />
            <textarea
              aria-label="Notes"
              placeholder="Notes"
              rows={1}
              value={draft.notes}
              onChange={(e) => update({ notes: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void submit();
                }
              }}
              className="field-sizing-content mt-1.5 block max-h-40 min-h-6 w-full resize-none bg-transparent text-[14px] leading-relaxed text-foreground/85 outline-none placeholder:text-subtle"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5 px-4 pb-3">
            <DestinationPicker value={draft.dest} onChange={(dest) => update({ dest })}>
              <button type="button" className={chipClass} aria-label={`Destination: ${destLabel(draft.dest, h)}`}>
                <DestIcon d={draft.dest} />
                <span className="truncate">{destLabel(draft.dest, h)}</span>
                <ChevronDown className="text-subtle" />
              </button>
            </DestinationPicker>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button type="button" className={chipClass} data-empty={draft.priority === "none"}>
                  <PriorityIcon priority={draft.priority} />
                  {draft.priority === "none" ? "Priority" : PRIORITY_LABEL[draft.priority]}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent onKeyDown={(e) => e.stopPropagation()}>
                <PriorityRadioItems value={draft.priority} onChange={(priority) => update({ priority })} />
              </DropdownMenuContent>
            </DropdownMenu>
            <DatePicker value={draft.when} today={h.today} onChange={(when) => update({ when })} clearLabel="No date">
              <button type="button" className={chipClass} data-empty={empty(draft.when)}>
                {draft.when === h.today ? <Star className="fill-current" /> : <CalendarDays />}
                {draft.when ? formatDay(draft.when, h.today) : "When"}
              </button>
            </DatePicker>
            <DatePicker value={draft.deadline} today={h.today} onChange={(deadline) => update({ deadline })} clearLabel="No deadline">
              <button type="button" className={chipClass} data-empty={empty(draft.deadline)}>
                <Flag />
                {draft.deadline ? `Due ${formatDay(draft.deadline, h.today)}` : "Deadline"}
              </button>
            </DatePicker>
            <ReminderPicker value={draft.reminderAt} onChange={(reminderAt) => update({ reminderAt })}>
              <button type="button" className={chipClass} data-empty={empty(draft.reminderAt)}>
                <Bell />
                {draft.reminderAt ? formatInstant(draft.reminderAt, h.timezone) : "Remind"}
              </button>
            </ReminderPicker>
          </div>
          {error && (
            <div role="alert" className="mx-5 mb-3 flex items-start gap-2 rounded-md bg-urgent-soft px-3 py-2 text-[13px] text-foreground">
              <span className="flex-1">{error} Your text is kept.</span>
              <button type="button" aria-label="Dismiss" onClick={() => setError(null)} className="text-muted-foreground">
                <X className="size-3.5" />
              </button>
            </div>
          )}
          <div className="flex items-center gap-2 border-t border-border px-4 py-2.5">
            <span className="text-[12px] text-subtle">
              <Kbd>↵</Kbd> save · <Kbd>esc</Kbd> close
            </span>
            <Button type="button" variant="ghost" size="sm" className="ml-auto" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" variant="default" size="sm" disabled={submitting || !draft.title.trim()} className={cn(submitting && "opacity-70")}>
              {submitting ? "Saving…" : "Add task"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
